/* eslint-disable no-nested-ternary */
import {
  AccessControl,
  AppContext,
  isAuthorised,
  getTranslatedText
} from '@nile/components';
import { DisabledTag } from '@nile/mangifera/src/Pebbles/TicketContainer';
import {
  Popover,
  Badge,
  ActionList,
  Button,
  TextStyle,
  Modal,
  TextContainer
} from '@superops/ui-design-system';
import React, { useCallback, useContext, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import PatchBulkActionNew from '@nile/rmm/src/modules/patch-management/components/sub-pages/PatchBulkActionsNew';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { SVGIcon } from '@superops/super-icons';
import { WINDOWS_POLICY_SET_ID } from '@nile/banyan/src/Constants/policySet';

const PatchPopoverBlock = (props) => {
  const { patch, intl } = props;
  const isWindows =
    (patch?.assetClass?.classId || patch?.patchDetail?.assetClass?.classId) ===
      WINDOWS_POLICY_SET_ID ||
    patch?.workStation?.assetClass?.classId === WINDOWS_POLICY_SET_ID;

  const isUninstallable =
    patch?.patchUninstallable || patch?.assetUninstallable;
  const isPatchUninstallable = isWindows && isUninstallable;
  const enableReboot =
    isPatchUninstallable &&
    ['Installed', 'Failed'].includes(patch.installationStatus);
  const [popover, setPopover] = useState(false);
  const flags = useFlags();
  const togglePopover = useCallback(() => {
    setPopover(!popover);
  });

  const getItems = (patch) => {
    if (flags.patchAdhocInstall) {
      if (
        patch.approvalStatus === 'Approved' &&
        patch.installationStatus !== 'Installed'
      ) {
        return [
          {
            content: getTranslatedText(intl, 'rmm.patch.install.now'),
            onAction: () => {
              props.handleApproveInstall(patch);
              togglePopover();
            }
          },
          {
            content: getTranslatedText(intl, 'asset.details.patch.rejectBtn'),
            onAction: () => {
              props.handleReject(patch);
              togglePopover();
            }
          }
        ];
      }
      if (patch.approvalStatus === 'Deferred') {
        const actions = [
          {
            content: getTranslatedText(intl, 'asset.details.patch.approveBtn'),
            onAction: () => {
              props.handleApprove(patch);
              togglePopover();
            }
          },
          {
            content: getTranslatedText(intl, 'asset.details.patch.rejectBtn'),
            onAction: () => {
              props.handleReject(patch);
              togglePopover();
            }
          }
        ];

        if (patch.installationStatus !== 'Installed') {
          actions.push({
            content: getTranslatedText(intl, 'rmm.patch.approve.and.install'),
            onAction: () => {
              props.handleApproveInstall(patch);
              togglePopover();
            }
          });
        }

        if (enableReboot) {
          actions.push({
            content: getTranslatedText(intl, 'rmm.patch.rollback'),
            onAction: () => {
              props.handleRollback(patch);
              togglePopover();
            }
          });
        }

        return actions;
      }
    }

    if (patch.approvalStatus === 'Approved') {
      const actions = [
        {
          content: getTranslatedText(intl, 'asset.details.patch.rejectBtn'),
          onAction: () => {
            props.handleReject(patch);
            togglePopover();
          }
        }
      ];

      if (enableReboot) {
        actions.push({
          content: getTranslatedText(intl, 'rmm.patch.rollback'),
          onAction: () => {
            props.handleRollback(patch);
            togglePopover();
          }
        });
      }

      return actions;
    }
    const approvalActions = [
      {
        content: getTranslatedText(intl, 'asset.details.patch.approveBtn'),
        onAction: () => {
          props.handleApprove(patch);
          togglePopover();
        }
      }
    ];
    if (flags.patchAdhocInstall) {
      if (patch.installationStatus !== 'Installed') {
        approvalActions.push({
          content: getTranslatedText(intl, 'rmm.patch.approve.and.install'),
          onAction: () => {
            props.handleApproveInstall(patch);
            togglePopover();
          }
        });
      }

      if (enableReboot) {
        approvalActions.push({
          content: getTranslatedText(intl, 'rmm.patch.rollback'),
          onAction: () => {
            props.handleRollback(patch);
            togglePopover();
          }
        });
      }
    }
    return approvalActions;
  };

  const activator = (
    <div className="flex actr ml-5">
      <Button
        plainSecondary
        icon={<SVGIcon name="Menu" size="md" />}
        onClick={togglePopover}
        pressed
      />
    </div>
  );

  return (
    <Popover active={popover} activator={activator} onClose={togglePopover}>
      <ActionList items={getItems(patch)} />
    </Popover>
  );
};

function PatchActionsCell(props) {
  const { cell, intl, disable = false } = props;
  const patchDetails = cell?.row?.original?.patchDetail || cell?.row?.original;
  const isWindows =
    patchDetails?.assetClass?.classId === WINDOWS_POLICY_SET_ID ||
    patchDetails?.workStation?.assetClass?.classId === WINDOWS_POLICY_SET_ID;

  const actions = cell.column.actions;

  const { editable = true } = cell.column;
  const flags = useFlags();
  const { field, meta = {} } = cell.column;

  const appContext = useContext(AppContext);

  const [popoverActive, setPopoverActive] = useState(false);
  const [rollbackModalActive, setRollbackModalActive] = useState(false);

  const togglePopoverActive = () =>
    setPopoverActive((popoverActive) => !popoverActive);

  const userRole = appContext?.user?.role?.roleFeatureList || [];

  function getApproveIcon() {
    return (
      <div className="fx center" style={{ marginRight: '8px' }}>
        <SVGIcon name="Resolution" color="#4F712B" size="sm" />
      </div>
    );
  }

  function getRejectIcon() {
    return (
      <div className="fx center mr8">
        <SVGIcon name="RejectedCircle" color="#B63535" size="sm" />
      </div>
    );
  }

  function getDeferredIcon() {
    return (
      <div className="flex mr8">
        <SVGIcon name="EstimatedTime" size="sm" />
      </div>
    );
  }
  function getStatusIcon(status) {
    switch (status) {
      case 'Approved':
        return getApproveIcon();
      case 'Rejected':
        return getRejectIcon();
      case 'Deferred':
        return getDeferredIcon();
      default:
        return '';
    }
  }
  function getBadgeStatus(status) {
    if (status === 'Approved') {
      return 'success';
    }
    if (status === 'Rejected') {
      return 'critical';
    }
    return '';
  }
  function renderStatus(status) {
    return (
      <Badge status={getBadgeStatus(status)} size="large" uppercase>
        {getStatusIcon(status)} {status}
      </Badge>
    );
  }

  const handleApprove = () => {
    actions.handleApprove(cell.row.original);
  };
  const handleRollback = () => {
    actions.handleRollback(cell.row.original);
  };

  const handleReject = () => {
    actions.handleReject(cell.row.original);
  };
  const handleApproveInstall = () => {
    actions.handleApproveInstall(cell.row.original);
  };
  const handleApproval = (type) => {
    switch (type) {
      case 'Approve':
        handleApprove();
        break;
      case 'Reject':
        handleReject();
        break;
      case 'ApproveInstall':
        handleApproveInstall();
        break;
      case 'Rollback':
        handleRollback();
        break;
      default:
        break;
    }
  };
  const disabledActions = [];
  const original = cell?.row?.original;

  if (
    ('patchUninstallable' in original && !original.patchUninstallable) ||
    ('assetUninstallable' in original && !original.assetUninstallable)
  ) {
    disabledActions.push('rollback');
  }
  return cell.value ? (
    <div>
      {cell.value === 'Pending' ? (
        isAuthorised(userRole, 'approveRejectPatches.allow') && editable ? (
          <div className="min-height-40 flex actr">
            {/* <div className="take-action" title="Take action">
              <TextStyle variation="subdued">
                <FormattedMessage id="asset.patchView.approvalStatus.takeAction" />
              </TextStyle>
            </div> */}
            {!meta?.noActions && (
              <div className="hiddenAction multi-action">
                <PatchBulkActionNew
                  actionClick={handleApproval}
                  records={[cell.row.original]}
                  disable={disable}
                  disableActions={disabledActions}
                  hideActions={isWindows ? [] : ['rollback']}
                />
              </div>
            )}
          </div>
        ) : (
          <Badge size="large" uppercase>
            {getTranslatedText(intl, 'admin.mspDetails.cname.status.pending')}
          </Badge>
        )
      ) : (
        <div className="flex-row patch-status-block flex-align-center">
          <div>{renderStatus(cell.value)}</div>
          {!meta?.noActions && editable && !disable && (
            <AccessControl
              roleToCheck="approveRejectPatches.allow"
              render={
                <div className="action-button">
                  <PatchPopoverBlock
                    patch={cell.row.original}
                    handleApprove={() => {
                      if (popoverActive) {
                        togglePopoverActive();
                      }
                      handleApprove();
                    }}
                    handleReject={() => {
                      if (popoverActive) {
                        togglePopoverActive();
                      }
                      handleReject();
                    }}
                    handleApproveInstall={() => {
                      if (popoverActive) {
                        togglePopoverActive();
                      }
                      handleApproveInstall();
                    }}
                    handleRollback={() => {
                      if (popoverActive) {
                        togglePopoverActive();
                      }
                      handleRollback();
                    }}
                    intl={intl}
                  />
                </div>
              }
            />
          )}
        </div>
      )}
    </div>
  ) : (
    <DisabledTag>
      <FormattedMessage id="app.noData.message" />
    </DisabledTag>
  );
}

export default injectIntl(PatchActionsCell);
